import { CheckOutlined } from "@ant-design/icons"
import { Card, Flex, Image, Tag, Tooltip, Typography } from "antd"
import React from "react"

import { type Attachment } from "../../../../../../Album/types/attachment.types.ts"
import { AttachmentTags } from "../../../../constants/attachment-tags.tsx"
import { getFileIcon } from "../../../../helpers/file.tsx"

const { Text } = Typography

interface AttachmentSelectorCardProps {
  attachment: Attachment
  isSelected: boolean
  onToggleAttachment: (id: string) => void
}

export function AttachmentSelectorCard({
  attachment,
  isSelected,
  onToggleAttachment
}: AttachmentSelectorCardProps) {
  const isImage = attachment.mime_type.startsWith("image/")

  return (
    <Card
      hoverable
      onClick={() => onToggleAttachment(attachment?.id)}
      className={`relative cursor-pointer transition-all ${
        isSelected ? "ring-2 ring-primary" : ""
      }`}
      styles={{ body: { padding: 8 } }}
      cover={
        <>
          {isSelected && (
            <div className="absolute top-2 right-2 z-10 bg-primary/10 rounded-full w-6 h-6 flex items-center justify-center">
              <CheckOutlined className="text-white text-xs" />
            </div>
          )}
          {isImage ? (
            <div className="aspect-square overflow-hidden bg-gray-100">
              <Image
                rootClassName={"size-full"}
                alt={attachment.original_filename}
                src={attachment.file}
                className="object-cover w-full h-full"
                preview={false}
              />
            </div>
          ) : (
            <div className="aspect-square bg-primary/10 flex items-center justify-center">
              {getFileIcon(attachment?.mime_type)}
            </div>
          )}
        </>
      }
    >
      <div className="flex flex-col gap-1">
        <Flex className={"flex"}>
          <Text
            ellipsis={{ tooltip: attachment?.original_filename }}
            className="text-xs"
            strong
          >
            {attachment?.original_filename}
          </Text>
          {attachment?.formatted_file_size && (
            <Text type="secondary" className="text-xs shrink-0">
              ({attachment?.formatted_file_size})
            </Text>
          )}
        </Flex>

        {attachment?.created && (
          <Text type="secondary" className="text-xs">
            {attachment.created}
          </Text>
        )}
        <Flex className={"gap-y-1"} wrap>
          <Tooltip title={attachment?.model_type_display}>
            <Tag
              className={"inline-flex w-fit max-w-full truncate"}
              color={AttachmentTags?.[attachment?.model_type_display]}
            >
              <Text
                className={"text-inherit text-xs"}
                ellipsis={{
                  tooltip: {
                    placement: "bottom",
                    title: attachment?.related_object_name
                  }
                }}
              >
                {attachment?.related_object_name}
              </Text>
            </Tag>
          </Tooltip>

          {attachment?.created_by_name && (
            <Tooltip title={`Uploaded by: ${attachment?.created_by_name}`}>
              <Tag
                className={"inline-flex w-fit max-w-full truncate"}
                color={"blue"}
              >
                <Text className={"text-inherit text-xs"} ellipsis>
                  {attachment?.created_by_name}
                </Text>
              </Tag>
            </Tooltip>
          )}
        </Flex>
      </div>
    </Card>
  )
}
